/* eslint-disable max-lines-per-function */
import { useMemo } from 'react';

import { useLocation } from 'react-router';

import type { TranslationKey } from '@change-corgi/core/i18n';
import { useNavigate } from '@change-corgi/core/react/router';
import { useTracking } from '@change-corgi/core/react/tracking';
import type { ExportPetitionContentType } from '@change-corgi/middle-end-gql-schema';

import { useStarterDashboardV2PageData } from 'src/app/pages/starterDashboardV2/pageContext';
import { isError, isLoaded, isLoading } from 'src/app/shared/utils/async';

import { EXPORT_ERROR_MAP, EXPORT_OPTIONS, EXPORT_SUCCESS_CODE } from '../../constants';
import { usePetitionExport } from '../usePetitionExport';

type ExportContentType = 'comments' | 'signatures';

type UseExportButtonReturn = ModelHookResult<
	{
		shouldHideExport: boolean;
		loading: boolean;
		errorMessage: TranslationKey | null;
		successMessage: TranslationKey | null;
		type: ExportContentType | null;
	},
	{ handleExport: (type: ExportPetitionContentType) => Promise<void> }
>;

export function useExportButton(exportContentType: ExportContentType): UseExportButtonReturn {
	const {
		data: {
			petition,
			featureConfigs: { exportSignatureBlocklist },
		},
	} = useStarterDashboardV2PageData();

	const track = useTracking();

	const shouldHideExport = useMemo(() => {
		if (exportSignatureBlocklist?.countryCodeIds.includes(petition.country.countryCode)) return true;
		if (exportSignatureBlocklist?.petitionIds.includes(petition.id)) return true;

		return false;
	}, [exportSignatureBlocklist, petition.country.countryCode, petition.id]);

	const navigate = useNavigate();
	const { pathname, search } = useLocation();
	const { mutate, reset, ...exportState } = usePetitionExport({
		onSuccess: (data) => {
			if (exportContentType === 'comments') {
				void track('petition_download_trigger', {
					type: 'comments',
					jobId: data.jobId,
				});
			} else {
				void track('petition_download_trigger', {
					type: 'signatures',
					signature_count: petition.signatureState.signatureCount.displayed,
					jobId: data.jobId,
				});
			}
		},
		onError: (err) => {
			void track('petition_download_error', {
				type: exportContentType,
			});
			if (err === EXPORT_ERROR_MAP.UNAUTHORIZED.code) {
				const redirectUrl = encodeURIComponent(pathname + search);
				navigate(`/login_or_join/redirected?redirectTo=${redirectUrl}`);
			}
		},
	});
	const handleExport = async (type: ExportPetitionContentType): Promise<void> => {
		// TODO: validate type better (zod?)
		const validatedType = EXPORT_OPTIONS.find((val) => val.value === type)?.value as ExportPetitionContentType;

		await mutate({ id: petition.id, type: validatedType, format: 'CSV', createdAt: petition.createdAt });
	};

	const successMessage = useMemo(() => {
		if (isLoaded(exportState)) return EXPORT_SUCCESS_CODE;
		return null;
	}, [exportState]);

	const errorMessage = useMemo(() => {
		if (isError(exportState)) return EXPORT_ERROR_MAP[exportState.error].message;
		return null;
	}, [exportState]);

	return {
		data: { shouldHideExport, loading: isLoading(exportState), errorMessage, successMessage, type: exportContentType },
		actions: { handleExport },
	};
}
